import React, { FocusEventHandler } from "react"
import { RequiredSymbol } from "./various"
import { debounce } from "lodash";
import { AbstractStore } from "../AbstractStores";
import { DropdownList } from "./dropdownList";
import { InputError } from "./inputError";

interface AutocompleteInputProps<T> {
	name?: string;
	error?: string;
	list: Array<T>;
	label?: string;
	value: string;
	onChange: (description: string) => void;
	onSelect: (item: T | null) => void;
	style?: React.CSSProperties;
	busy?: boolean;
	required?: boolean;
	readOnly?: boolean;
	onFocus?: FocusEventHandler;
	onBlur?: FocusEventHandler;
	textSelector: (item: T) => React.ReactNode;
	idSelector: (item: T) => string | number;
	placeholder?: string;
	ulStyle?: React.CSSProperties;
}


class AutocompleteInputInternal<T> extends React.Component<AutocompleteInputProps<T>>
{
	private input = React.createRef<HTMLInputElement>();
	private container = React.createRef<HTMLDivElement>();

	private onChange = (event: React.FormEvent<HTMLInputElement>) => {
		var description = (event.target as any).value;
		if (description == "" && !this.props.required)
			this.props.onSelect(null);
		else
			this.props.onChange(description);
	}

	componentDidMount() {
		this.container.current!.addEventListener("keydown", this.handleKeyDown, false);
	}

	componentWillUnmount() {
		this.container.current!.removeEventListener("keydown", this.handleKeyDown, false);
	}

	public focus = () => {
		this.input.current!.focus();
	}

	handleKeyDown = (e: KeyboardEvent) => {
		switch (e.key) {
			case "ArrowUp":
				this.dropDownMenu.current?.moveUp();
				break;
			case "ArrowDown":
				this.dropDownMenu.current?.moveDown();
				break;
			case "Enter":
				this.dropDownMenu.current?.select();
				e.preventDefault();
				break;
			case "Tab":
				this.dropDownMenu.current?.select();
				break;
		}
	}

	private onFocus = (event: React.FocusEvent) => {
		this.props.onFocus && this.props.onFocus(event);
	}

	private onBlur = (event: React.FocusEvent) => {
		this.props.onBlur && this.props.onBlur(event);
	}

	private onSelect = (v: T | null) => {
		if (v == null) {
			this.props.onSelect(null);
		} else {
			const index = this.props.list.indexOf(v);
			if (index > -1) {
				const value = this.props.list[index];
				this.props.onSelect(value);
			}
		}
	};

	private dropDownMenu = React.createRef<DropdownList<T, {}>>();
	render() {
		var wrapperClass = this.props.label ? "mb-3" : "";
		const divStyle = { ...(this.props.style || {}), width: "100%" };
		var required = this.props.required ? <RequiredSymbol /> : undefined;
		return (<div style={{ position: "relative" }} className={wrapperClass}>
			{this.props.label && <label className="form-label" htmlFor={this.props.name} onClick={() => this.focus()}>{this.props.label}</label>} {required}
			<div
				ref={this.container}
				className={`form-control bncombo ${this.props.readOnly ? "readonly" : ""} pe-1`}
				style={divStyle}>
				<input
					type="text"
					style={{ width: "100%", border: "none" }}
					name={this.props.name}
					ref={this.input}
					value={this.props.value}
					onChange={this.props.readOnly ? () => { } : this.onChange}
					onFocus={this.props.readOnly ? () => { } : this.onFocus}
					onBlur={this.props.readOnly ? () => { } : this.onBlur}
					autoComplete="chrome-off"
					readOnly={this.props.readOnly}
					required={this.props.required}
					placeholder={this.props.placeholder}
				/>
				{!(!this.props.value || this.props.readOnly) && <a onClick={() => this.onSelect(null)}
					title="Rimuovi selezione"
					style={{ width: 10, marginRight: 20, textAlign: "center", cursor: "pointer" }}>
					<i className="fas fa-times text-danger" />
				</a>}
				{
					this.props.busy
						? <div style={{ width: 10, marginRight: 10, textAlign: "center", cursor: "pointer" }}>
							<i title="Caricamento in corso" className="fas fa-sync fa-spin" />
						</div>
						: null
				}

				{this.props.children}
			</div>
			<DropdownList<T, {}>
				ref={this.dropDownMenu}
				items={this.props.list}
				itemComponent={this.props.textSelector}
				onSelect={this.onSelect}
				isOpen
				ulStyle={this.props.ulStyle}
			/>
			<InputError error={this.props.error} />
		</div >
		);
	}
}


class AutocompleteStore<T> extends AbstractStore<T> {
	searched: { [index: string]: T[] } = {};
	constructor(idSelector: (item: T) => string) {
		super(idSelector);
	}

	getSearchResult(search: string) {
		return this.searched[search.toLowerCase()] ?? [];
	}
}


interface Props<T> {
	error?: string;
	onChange: (item: T | null) => boolean | void;
	hidden?: boolean;
	name?: string;
	placeholder?: string;
	label?: string;
	style?: React.CSSProperties;
	readOnly?: boolean;
	value?: string;
	required?: boolean;
	onFocus?: FocusEventHandler;
	onBlur?: FocusEventHandler;
	minLength?: number;
	textSelector: (item: T) => string;
	itemComponent?: (item: T) => React.ReactNode;
	idSelector: (item: T) => string | number;
	ActionsGet: (id: string) => Promise<T | null>;
	ActionsSearch: (literal: string) => Promise<T[]>;
	ulStyle?: React.CSSProperties;
}
interface State<T> {
	busy: boolean;
	data: T[];
	text: string;
}

export class Autocomplete<T> extends React.Component<Props<T>, State<T>> {

	private store: AutocompleteStore<T>;

	constructor(props: Props<T>) {
		super(props);
		this.store = new AutocompleteStore<T>(x => props.idSelector(x)?.toString() ?? null);
		const item = props.value && this.store.get(props.value);
		let nomeT = item && this.props.textSelector(item) || "";
		if (props.readOnly && props.value)
			nomeT = props.value;
		this.state = {
			busy: false,
			data: [],
			text: nomeT,
		};
	}

	private RequestItem = async (value: string) => {
		const item = await this.props.ActionsGet(value);
		if (item) {
			this.store.updateOrAdd(item);
			let text = "";
			if (this.props.value) {
				const item = this.store.get(this.props.value);
				text = item && this.props.textSelector(item) || "";
			}
			this.setState({
				text,
				data: [],
				busy: false,
			});
		}
	}

	private SearchItems = debounce(async (literal: string) => {
		const minLenght = this.props.minLength == null || this.props.minLength <= 0 ? 0 : this.props.minLength;

		if (literal.length >= minLenght) {
			const data = await this.props.ActionsSearch(literal);
			this.store.searched[literal.toLowerCase()] = data;
			this.store.updateOrAddAll(data);
			const { text } = this.state;
			this.setState({
				data: text ? this.store.getSearchResult(text) : [],
				busy: false,
			});
		} else {
			this.setState({ busy: false });
		}
	}, 300);

	async componentDidMount() {
		const item = this.props.value && this.store.get(this.props.value);
		if (!item && this.props.value) {
			this.RequestItem(this.props.value);
		}
	}

	componentDidUpdate(oldProps: Props<T>) {
		const item = this.props.value && this.store.get(this.props.value);
		if (!item && this.props.value) {
			this.RequestItem(this.props.value);
		}
		if (oldProps.value && !this.props.value && this.state.text)
			this.setState({ text: "" })

	}

	private search = (text: string) => {
		var data: T[] = [];
		if (text !== "") {
			data = this.store.getSearchResult(text);
			if (data.length === 0) {
				this.setState({ busy: true });
				this.SearchItems(text);
			}
		}
		this.setState({ data });
	}

	private onChange = (text: string) => {
		this.search(text);
		this.setState({ text });
	}

	private onSelect = (item: T | null) => {
		const reset = this.props.onChange(item);
		this.setState({
			text: reset ? "" : (item && this.props.textSelector(item) || ""),
			data: [],
			busy: false,
		});
	}

	private onBlur = (e: React.FocusEvent) => {
		setTimeout(() => {
			const item = this.props.value && this.store.get(this.props.value);
			const text = item && this.props.textSelector(item) || "";
			this.setState({ text, data: [] })
		}, 300);
		this.props.onBlur?.(e)
	}

	render() {
		var component = (
			<AutocompleteInputInternal<T>
				style={this.props.style}
				name={this.props.name}
				label={this.props.label || "T"}
				value={this.state.text}
				error={this.props.error}
				list={this.state.data}
				onChange={this.onChange}
				onSelect={this.onSelect}
				textSelector={this.props.itemComponent ?? this.props.textSelector}
				idSelector={this.props.idSelector}
				busy={this.state.busy}
				required={this.props.required}
				onBlur={this.onBlur}
				onFocus={this.props.onFocus}
				readOnly={this.props.readOnly}
				placeholder={this.props.placeholder}
				ulStyle={this.props.ulStyle}
			>
				{this.props.children}
			</AutocompleteInputInternal>
		);
		if (this.props.hidden === true) {
			component = (<div />);
		}
		return (component);
	}
}